import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getInterviews, createInterview } from "../../../_api/Talent";
import { getUsers } from "../../../_api/Admin";
import { Loader } from "../../../_helpers/components/Forms/Loader";
import { Button } from "../../../_helpers/components/Button";
import { SideBar } from "../../../_helpers/components/SideBar";

const TYPE_LABELS = { ANNUEL: "Annuel", PROFESSIONNEL: "Professionnel", EVAL_360: "Évaluation 360°", RECRUTEMENT: "Recrutement" };

const formatDate = (d) => {
    if (!d) return "";
    const x = new Date(d);
    return x.toLocaleDateString("fr-FR");
};

export const Entretiens = () => {
    const navigate = useNavigate();
    const [interviews, setInterviews] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ type: "ANNUEL", date: "", time: "", user_id: "", evaluator_id: "" });
    const [openSidebar, setOpenSidebar] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetch = () => {
        setLoading(true);
        Promise.all([getInterviews(), getUsers()])
            .then(([iRes, uRes]) => {
                setInterviews(iRes.data || []);
                setUsers(uRes.data || []);
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.date) return;
        setIsSubmitting(true);
        createInterview({ ...form, user_id: form.user_id || undefined, evaluator_id: form.evaluator_id || undefined, time: form.time || undefined })
            .then(() => {
                toast.success("Entretien créé.");
                setForm({ type: "ANNUEL", date: "", time: "", user_id: "", evaluator_id: "" });
                setOpenSidebar(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setIsSubmitting(false));
    };

    const activeUsers = users.filter((u) => u.activated !== false);

    if (loading) return <Loader text="Chargement..." />;
    if (error) return <div className="min-h-full bg-gray-50 flex items-center justify-center"><div className="text-red-600">{error}</div></div>;

    const InterviewForm = () => (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="ANNUEL">Annuel</option>
                    <option value="PROFESSIONNEL">Professionnel</option>
                    <option value="EVAL_360">Évaluation 360°</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure</label>
                <input type="time" value={form.time || ""} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collaborateur</label>
                <select value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="">—</option>
                    {activeUsers.map((u) => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Évaluateur</label>
                <select value={form.evaluator_id} onChange={(e) => setForm((f) => ({ ...f, evaluator_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="">—</option>
                    {activeUsers.map((u) => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                    ))}
                </select>
            </div>
            <div className="flex gap-2 pt-2">
                <Button buttonType="submit" name="Ajouter" loading={isSubmitting} />
                <Button type="empty" name="Annuler" action={() => setOpenSidebar(false)} />
            </div>
        </form>
    );

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Gestion des entretiens</h1>
                            <p className="mt-2 text-sm text-gray-600">Planifiez et suivez vos entretiens annuels et d'évaluation</p>
                        </div>
                        <Button action={() => setOpenSidebar(true)} name="Nouvel entretien" />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg">
                                <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Entretiens</h2>
                                <p className="text-sm text-gray-500">{interviews.length} entretien{interviews.length > 1 ? "s" : ""}</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        {interviews.length > 0 ? (
                            <ul className="space-y-3">
                                {interviews.map((i) => (
                                    <li
                                        key={i.id}
                                        onClick={() => navigate(`/talents/entretiens/${i.id}`)}
                                        className="flex items-start gap-3 py-3 px-4 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full text-gray-600 font-medium text-xs shrink-0">
                                            {(i.user?.first_name || i.candidate?.first_name)?.[0] || "-"}
                                            {(i.user?.last_name || i.candidate?.last_name)?.[0] || ""}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900">{TYPE_LABELS[i.type] || i.type}</p>
                                            <p className="text-sm text-gray-600">
                                                {i.user ? `${i.user.first_name} ${i.user.last_name}` : i.candidate ? `${i.candidate.first_name} ${i.candidate.last_name}` : "-"}
                                            </p>
                                            <p className="text-xs text-gray-500">{formatDate(i.date)}{i.evaluator ? ` · Évaluateur : ${i.evaluator.first_name} ${i.evaluator.last_name}` : ""}</p>
                                        </div>
                                        <span className="text-xs text-app-primary shrink-0">Voir →</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center py-8">
                                <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <h3 className="mt-4 text-sm font-medium text-gray-900">Aucun entretien</h3>
                                <p className="mt-2 text-sm text-gray-500">Cliquez sur « Nouvel entretien » pour commencer.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SideBar title="Nouvel entretien" content={<InterviewForm />} isOpen={openSidebar} onClose={() => setOpenSidebar(false)} />
        </div>
    );
};
